import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { field } from "@/components/ui-kit";

const HERO = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70";

const RoleCard = ({ role, title, desc, Icon, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`quick-login-${role}`}
    className="group flex w-full items-center gap-4 rounded-sm border border-slate-700 bg-[#252A35] p-4 text-left transition-colors hover:border-emerald-500/50 hover:bg-[#2D3340]"
  >
    <div className="grid h-10 w-10 place-items-center rounded-sm bg-emerald-500/10 text-emerald-300">
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex-1">
      <div className="font-heading text-sm font-semibold text-slate-100">{title}</div>
      <div className="text-xs text-slate-400">{desc}</div>
    </div>
    <ArrowRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-1 group-hover:text-emerald-300" />
  </button>
);

export default function Login() {
  const { user, signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [quickOn, setQuickOn] = useState(false);
  useEffect(() => {
    api.get("/auth/config").then((r) => setQuickOn(r.data.quick_login)).catch(() => setQuickOn(false));
  }, []);
  if (user) return <Navigate to="/" replace />;

  const quick = async (role) => {
    try {
      signIn((await api.post("/auth/quick-login", { role })).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      signIn((await api.post("/auth/login", { email, password })).data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="grid min-h-screen bg-[#17191F] lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src={HERO} alt="Warehouse racking" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <img src="/brand/ep_logo_red.png" alt="EP Group 50 years" className="absolute left-12 top-12 z-10 h-12 w-auto" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#17191F] via-[#17191F]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <ul className="grid grid-cols-1 gap-1 font-mono text-[11px] uppercase tracking-widest text-emerald-400" data-testid="login-edi-codes">
            {[["850", "Purchase Order"], ["940", "Warehouse Shipping Order"], ["945", "Warehouse Shipping Advice"], ["861", "Receiving Advice"], ["856", "Advance Ship Notice"]].map(([c, t]) => (
              <li key={c}><span className="text-slate-100">{c}</span> <span className="text-slate-400">· {t}</span></li>
            ))}
          </ul>
          <h2 className="mt-3 font-heading text-4xl font-semibold leading-tight text-slate-100">
            Orders, EDI and xDock stock in one place.
          </h2>
          <p className="mt-3 text-sm text-slate-400">Euro Packaging</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div>
            <img src="/brand/ep_logo_white.png" alt="EP make it easy" className="mb-6 h-10 w-auto" data-testid="login-logo" />
            <h1 className="font-heading text-3xl font-semibold text-slate-100">Sign in</h1>
            <p className="mt-1 text-sm text-slate-400">Euro Packaging EDI Order Hub</p>
          </div>
          {quickOn && (
          <div className="space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Quick login</div>
            <RoleCard role="processor" title="Order Processor" desc="Import EDI, prepare orders, ship replenishment" Icon={ClipboardList} onClick={() => quick("processor")} />
            <RoleCard role="manager" title="Manager" desc="Approve orders, release 940s and call-offs" Icon={ShieldCheck} onClick={() => quick("manager")} />
          </div>
          )}
          <form onSubmit={submit} className={`space-y-3 ${quickOn ? "border-t border-slate-800 pt-6" : ""}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{quickOn ? "Or email & password" : "Email & password"}</div>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" className={field} data-testid="login-email-input" />
            <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" type="password" className={field} data-testid="login-password-input" />
            <Button type="submit" className="w-full bg-slate-100 text-slate-950 hover:bg-white" data-testid="login-submit-btn">
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
